import React from "react";
import Link from "next/link";
import { useTranslation } from "../../../../i18n/client";
import LogoDark from "../../../../../public/images/New-landing/logo-dark.svg";
import { Routes } from "../../../(utils)/routes";

interface FooterProps {
  lng: string;
}

const Footer: React.FC<FooterProps> = ({ lng }) => {
  const { t }: { t: any } = useTranslation(lng, "new-landing");
  return (
    <footer className="w-full bg-footer-gradient shadow-[0_-3px_19px_2px_rgba(0,0,0,0.1)] pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-8">
        <div className="mb-8">
          <Link href={`/${Routes.Home}`}>
            <LogoDark width={102} height={27} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 mb-6">
          <div className="lg:col-span-1">
            <h3 className="text-primary-dark font-bold text-[18px] mb-3">
              {t("footer.tmg-title")}
            </h3>
            <div className="text-primary-dark font-medium text-[14px] leading-5">
              <p>{t("footer.company-name")}</p>
              <p>{t("footer.address")}</p>
              <p>{t("footer.city")}</p>
            </div>
          </div>

          <div className="lg:col-span-1">
            <h3 className="hidden sm:block text-primary-dark font-bold text-[18px] mb-3 invisible">
              &nbsp;
            </h3>
            <div className="text-primary-dark font-medium text-[14px] leading-5">
              <p>{t("footer.court")}</p>
              <p>{t("footer.tax-id")}</p>
              <p>{t("footer.tax-number")}</p>
              <p>{t("footer.responsible")}</p>
            </div>
          </div>

          <div className="lg:col-span-1">
            <h3 className="text-primary-dark font-bold text-[18px] mb-4">
              {t("footer.contacts-title")}
            </h3>
            <div className="space-y-3">
              <p className="text-primary-dark font-medium text-[14px]">
                {t("footer.company-name")}, {t("footer.address")},{" "}
                {t("footer.city")}
              </p>
              <a
                href={`tel:${t("footer.phone")}`}
                className="block text-primary-dark font-medium text-[14px] hover:opacity-70 transition-opacity duration-200"
              >
                {t("footer.phone")}
              </a>
              <a
                href={`mailto:${t("footer.email")}`}
                className="block text-primary-dark font-medium text-[14px] hover:opacity-70 transition-opacity duration-200"
              >
                {t("footer.email")}
              </a>

              <div className="flex gap-3 mt-4">
                <Link
                  href="https://instagram.com/omtomedical"
                  aria-label="Visit OMTO Instagram page"
                >
                  <div className="w-8 h-8 bg-primary-dark rounded-full flex items-center justify-center hover:opacity-70 transition-opacity duration-200">
                    <svg
                      width="16"
                      height="15"
                      viewBox="0 0 16 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.2 0C12.2609 0 13.2783 0.421427 14.0285 1.17157C14.7786 1.92172 15.2 2.93913 15.2 4V10.4C15.2 11.4609 14.7786 12.4783 14.0285 13.2284C13.2783 13.9786 12.2609 14.4 11.2 14.4H4.80005C3.73918 14.4 2.72177 13.9786 1.97162 13.2284C1.22148 12.4783 0.800049 11.4609 0.800049 10.4V4C0.800049 2.93913 1.22148 1.92172 1.97162 1.17157C2.72177 0.421427 3.73918 0 4.80005 0H11.2ZM8.00005 4C7.15136 4 6.33742 4.33714 5.73731 4.93726C5.13719 5.53737 4.80005 6.35131 4.80005 7.2C4.80005 8.04869 5.13719 8.86263 5.73731 9.46274C6.33742 10.0629 7.15136 10.4 8.00005 10.4C8.84874 10.4 9.66267 10.0629 10.2628 9.46274C10.8629 8.86263 11.2 8.04869 11.2 7.2C11.2 6.35131 10.8629 5.53737 10.2628 4.93726C9.66267 4.33714 8.84874 4 8.00005 4ZM8.00005 5.6C8.42439 5.6 8.83136 5.76857 9.13142 6.06863C9.43148 6.36869 9.60005 6.77565 9.60005 7.2C9.60005 7.62435 9.43148 8.03131 9.13142 8.33137C8.83136 8.63143 8.42439 8.8 8.00005 8.8C7.5757 8.8 7.16874 8.63143 6.86868 8.33137C6.56862 8.03131 6.40005 7.62435 6.40005 7.2C6.40005 6.77565 6.56862 6.36869 6.86868 6.06863C7.16874 5.76857 7.5757 5.6 8.00005 5.6ZM11.6 2.8C11.3879 2.8 11.1844 2.88429 11.0344 3.03431C10.8843 3.18434 10.8 3.38783 10.8 3.6C10.8 3.81217 10.8843 4.01566 11.0344 4.16569C11.1844 4.31571 11.3879 4.4 11.6 4.4C11.8122 4.4 12.0157 4.31571 12.1657 4.16569C12.3158 4.01566 12.4 3.81217 12.4 3.6C12.4 3.38783 12.3158 3.18434 12.1657 3.03431C12.0157 2.88429 11.8122 2.8 11.6 2.8Z"
                        fill="white"
                      />
                    </svg>
                  </div>
                </Link>
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://www.linkedin.com/company/omtomedical/"
                  aria-label="Visit OMTO LinkedIn page"
                >
                  <div className="w-8 h-8 bg-primary-dark rounded-full flex items-center justify-center hover:opacity-70 transition-opacity duration-200">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                        fill="white"
                      />
                    </svg>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          <div className="flex flex-col sm:flex-row gap-8 md:gap-16">
            <div>
              <h3 className="text-primary-dark font-bold text-[18px] mb-3">
                {t("footer.represented-by")}
              </h3>
              <p className="text-primary-dark font-medium text-[14px]">
                {t("footer.represented-name")}
              </p>

              <h3 className="text-primary-dark font-bold text-[18px] mb-3 mt-6">
                {t("footer.register-title")}
              </h3>
              <p className="text-primary-dark font-medium text-[14px]">
                {t("footer.register-number")}
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-primary-dark font-bold text-[18px] mb-3">
              {t("footer.legal-title")}
            </h3>
            <div className="space-y-1">
              <div>
                <Link
                  href={`${Routes.PrivacyPolicyNew}`}
                  className="inline-block hover:opacity-70 transition-opacity duration-300 text-primary-dark font-semibold text-[14px] leading-6"
                >
                  {t("footer.data-compliance")}
                </Link>
              </div>
              <div>
                <Link
                  href={`${Routes.PrivacyPolicyNew}#cookies`}
                  className="inline-block hover:opacity-70 transition-opacity duration-300 text-primary-dark font-semibold text-[14px] leading-6"
                >
                  {t("footer.cookies")}
                </Link>
              </div>
              <div>
                <Link
                  href={`${Routes.Terms}`}
                  className="inline-block hover:opacity-70 transition-opacity duration-300 text-primary-dark font-semibold text-[14px] leading-6"
                >
                  {t("footer.terms")}
                </Link>
              </div>
              <div>
                <Link
                  href={`${Routes.TomList}`}
                  className="inline-block hover:opacity-70 transition-opacity duration-300 text-primary-dark font-semibold text-[14px] leading-6"
                >
                  {t("footer.tom-list")}
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center pt-8">
          <p className="text-primary-dark font-semibold text-base">
            {t("footer.copyright").replace("{{year}}", new Date().getFullYear().toString())}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
